/** Training image lightbox: one image at a time with its title and description, previous and next, a counter. */
import { Dialog } from 'radix-ui';
import { ChevronLeft, ChevronRight, X } from 'lucide-react';
import { IconButton } from '@/components/ui/primitives';
import type { TrainingImage } from '../../types';

export function Lightbox({ images, index, onIndex, onClose }: { images: TrainingImage[]; index: number | null; onIndex: (i: number) => void; onClose: () => void }) {
  const open = index !== null && index >= 0 && index < images.length;
  const current = open ? images[index] : null;
  return (
    <Dialog.Root open={open} onOpenChange={(o) => !o && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-black/70 backdrop-blur-sm" />
        <Dialog.Content
          aria-describedby={undefined}
          className="fixed left-1/2 top-1/2 z-50 flex w-[min(960px,calc(100vw-32px))] max-h-[calc(100vh-32px)] -translate-x-1/2 -translate-y-1/2 flex-col rounded-panel border border-hairline-strong bg-raised text-xs shadow-elev-2 focus:outline-none"
          onKeyDown={(e) => {
            if (!open) return;
            if (e.key === 'ArrowRight' && index < images.length - 1) onIndex(index + 1);
            if (e.key === 'ArrowLeft' && index > 0) onIndex(index - 1);
          }}
        >
          <div className="flex h-11 shrink-0 items-center justify-between border-b border-hairline px-4">
            <Dialog.Title className="truncate text-sm font-semibold text-ink">{current?.title || 'Training'}</Dialog.Title>
            <div className="flex items-center gap-1">
              <span className="font-mono text-[11px] text-ink-3">
                {open ? index + 1 : 0}/{images.length}
              </span>
              <IconButton label="Previous (Left)" disabled={!open || index === 0} onClick={() => open && onIndex(index - 1)}>
                <ChevronLeft className="h-4 w-4" />
              </IconButton>
              <IconButton label="Next (Right)" disabled={!open || index === images.length - 1} onClick={() => open && onIndex(index + 1)}>
                <ChevronRight className="h-4 w-4" />
              </IconButton>
              <Dialog.Close asChild>
                <IconButton label="Close (Esc)">
                  <X className="h-4 w-4" />
                </IconButton>
              </Dialog.Close>
            </div>
          </div>
          {current && (
            <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto p-4 scrollbar-thin">
              <img src={current.image} alt={current.title || 'Training image'} className="max-h-[60vh] w-full rounded-control object-contain" />
              {current.description && <p className="leading-relaxed text-ink-2">{current.description}</p>}
            </div>
          )}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
